import { DateTools } from '@treezor/design-system';

import {
  IKycBaasDocument,
  IKycConnectDocument,
  IKycDocument,
  KycConnectDocumentStatus,
} from '@/features/kyc/store/models/kyc.model';

export const KycTools = {
  convertKycBaasDocumentToKycDocument: (baasDocument: IKycBaasDocument): IKycDocument => ({
    documentId: baasDocument.documentId.toString(),
    documentType: baasDocument.documentTypeId,
    userId: baasDocument.userId,
    fileName: baasDocument.fileName,
    createdAt: baasDocument.createdDate,
    updatedAt: baasDocument.modifiedDate,
    treezorStatus: baasDocument.documentStatus,
    codeStatus: baasDocument.codeStatus,
    isAgent: false,
  }),
  convertKycConnectDocumentToKycDocument: (connectDocument: IKycConnectDocument): IKycDocument => ({
    documentId: connectDocument.documentId,
    documentType: connectDocument.documentType,
    userId: connectDocument.userId,
    createdAt: connectDocument.createdAt,
    updatedAt: connectDocument.updatedAt,
    comment: connectDocument.comment,
    status: KycConnectDocumentStatus[connectDocument.status],
    agentComment: connectDocument.comment,
    agentStatus: KycConnectDocumentStatus[connectDocument.status],
    isAgent: true,
    metadata: {
      treezorDocumentId: connectDocument.metadata?.treezor_document_id,
    },
  }),
  getComputedKycDocuments: (
    connectDocuments: IKycConnectDocument[],
    baasDocuments: IKycBaasDocument[],
  ): IKycDocument[] => {
    const connect = connectDocuments.map((connectDocument) =>
      KycTools.convertKycConnectDocumentToKycDocument(connectDocument),
    );
    const connectDocumentOnly = connect.filter(
      (document) =>
        !document.metadata?.treezorDocumentId && (document.status === 'PENDING' || document.status === 'REFUSED'),
    );

    const baas = baasDocuments.map((baasDocument) => {
      const convertedDoc = KycTools.convertKycBaasDocumentToKycDocument(baasDocument);
      const linkedDocument = connect.find(KycTools.findDocument(convertedDoc.documentId));
      const additionalProps = !!linkedDocument
        ? {
            agentStatus: linkedDocument?.agentStatus,
            agentComment: linkedDocument?.agentComment,
            metadata: { ...convertedDoc.metadata, connectDocumentId: linkedDocument?.documentId },
          }
        : {};

      return {
        ...convertedDoc,
        ...additionalProps,
      };
    });

    return [...connectDocumentOnly, ...baas].sort(
      (documentA, documentB) => DateTools.createDate(documentB?.createdAt) - DateTools.createDate(documentA?.createdAt),
    );
  },
  findDocument: (documentId: string) => (document: IKycDocument) =>
    String(document.metadata?.treezorDocumentId) === String(documentId),
  filterDocumentByStatus: (baasDocuments: IKycDocument[]) => (document: IKycDocument) => {
    const linkedDocument = baasDocuments.find(
      (bassDocument) => String(bassDocument.documentId) === String(document.metadata.treezorDocumentId),
    );

    return String(linkedDocument.codeStatus) === '600003';
  },
};
